import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactElement } from 'react';

import { useAuth } from '@/app/auth/context';
import { Modal } from '@/components/admin/Modal';
import { Notice } from '@/components/admin/Notice';
import {
  AUDIENCE_LABELS,
  CONDITION_LABELS,
} from '@/features/notifications/format';
import {
  SCHEDULE_STATUS_LABELS,
  cancelSchedule,
  createEventExtraSchedule,
  fetchSchedulePreview,
  fetchSchedules,
  rescheduleSchedule,
  setEventNotificationsSuppression,
  testSendSchedule,
  uploadNotificationAttachment,
  type NotificationAttachment,
  type NotificationSchedule,
  type SchedulePreview,
} from '@/features/notifications/api';
import { ApiError } from '@/lib/api/http';
import { datetimeLocalToIso, formatDateTime, toDatetimeLocalValue } from '@/lib/datetime';

const EXTRA_AUDIENCES = ['participants', 'directors', 'all_students', 'success', 'office'] as const;

/** イベント詳細の「通知」タブ（この回のメール一覧 / D-247・D-355） */
export function EventNotificationsTab({
  eventId,
  notificationsSuppressed,
}: {
  eventId: string;
  notificationsSuppressed: boolean;
}): ReactElement {
  const { user } = useAuth();
  const isOffice = user?.active_role === 'office';
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['notification-schedules', eventId],
    queryFn: () => fetchSchedules({ eventId, perPage: 100 }),
    enabled: isOffice,
  });

  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<SchedulePreview | null>(null);
  const [busy, setBusy] = useState(false);
  const [suppressed, setSuppressed] = useState(notificationsSuppressed);
  const [rescheduleTarget, setRescheduleTarget] = useState<NotificationSchedule | null>(null);
  const [rescheduleAt, setRescheduleAt] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [addAudience, setAddAudience] = useState<string>('participants');
  const [addSubject, setAddSubject] = useState('');
  const [addBody, setAddBody] = useState('');
  const [addSenderName, setAddSenderName] = useState('');
  const [addAt, setAddAt] = useState('');
  const [addFile, setAddFile] = useState<File | null>(null);

  if (!isOffice) {
    return <p className="text-wptext-muted">通知の管理は事務局のみ行えます。</p>;
  }
  if (query.isPending) return <p className="text-wptext-muted">読み込み中…</p>;
  if (query.isError) return <p className="text-wpnotice-error">読み込みに失敗しました。</p>;

  const schedules = query.data.items;

  async function refresh(): Promise<void> {
    await queryClient.invalidateQueries({ queryKey: ['notification-schedules', eventId] });
  }

  function resetAddForm(): void {
    setAddAudience('participants');
    setAddSubject('');
    setAddBody('');
    setAddSenderName('');
    setAddAt('');
    setAddFile(null);
  }

  async function onToggleSuppression(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const result = await setEventNotificationsSuppression(eventId, !suppressed);
      setSuppressed(result.suppressed);
      setNotice(result.suppressed ? 'このイベントの通知を停止しました。' : '通知の停止を解除しました。');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '変更に失敗しました。');
    } finally {
      setBusy(false);
    }
  }

  async function onPreview(schedule: NotificationSchedule): Promise<void> {
    setError(null);
    try {
      setPreview(await fetchSchedulePreview(schedule.id));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'プレビューの取得に失敗しました。');
    }
  }

  async function onTestSend(schedule: NotificationSchedule): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const result = await testSendSchedule(schedule.id);
      setNotice(`テスト送信しました（宛先: ${result.sent_to}。本番宛先には送っていません）。`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'テスト送信に失敗しました。');
    } finally {
      setBusy(false);
    }
  }

  async function onCancel(schedule: NotificationSchedule): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await cancelSchedule(schedule.id);
      await refresh();
      setNotice('この回の配信をキャンセルしました。');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'キャンセルに失敗しました。');
    } finally {
      setBusy(false);
    }
  }

  async function onReschedule(): Promise<void> {
    if (!rescheduleTarget || !rescheduleAt) return;
    setBusy(true);
    setError(null);
    try {
      await rescheduleSchedule(rescheduleTarget.id, datetimeLocalToIso(rescheduleAt));
      await refresh();
      setRescheduleTarget(null);
      setNotice('この回だけの配信時刻を変更しました（種類の自動配信ルール自体は変わっていません）。');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '時刻の変更に失敗しました。');
    } finally {
      setBusy(false);
    }
  }

  async function onAddExtra(): Promise<void> {
    if (!addSubject.trim() || !addBody.trim() || !addAt) return;
    setBusy(true);
    setError(null);
    try {
      let attachment: NotificationAttachment | null = null;
      if (addFile) {
        attachment = await uploadNotificationAttachment(addFile);
      }
      await createEventExtraSchedule({
        event_id: eventId,
        audience_key: addAudience,
        subject: addSubject.trim(),
        body: addBody.trim(),
        sender_name: addSenderName.trim() || null,
        scheduled_for: datetimeLocalToIso(addAt),
        attachment_key: attachment?.key ?? null,
        attachment_name: attachment?.filename ?? null,
        attachment_size: attachment?.size ?? null,
      });
      await refresh();
      setAddOpen(false);
      resetAddForm();
      setNotice('この回だけ、メールを1通追加しました。');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '追加に失敗しました。');
    } finally {
      setBusy(false);
    }
  }

  const canAdd = addSubject.trim().length > 0 && addBody.trim().length > 0 && Boolean(addAt);

  return (
    <div>
      {notice && (
        <Notice kind="success" onDismiss={() => setNotice(null)}>
          {notice}
        </Notice>
      )}
      {error && (
        <Notice kind="error" dismissible={false}>
          {error}
        </Notice>
      )}

      <p className="mb-3 text-wptext-muted">
        登録時に種類の自動配信ルールから予約された、この回のメールです。時刻の変更・追加・キャンセルはこの回だけに効きます。開催日だけ変えたときは、手で直した日時も同じ日数ずれます。開催時刻を変えると、手で直した日時は自動計算に戻ります。種類ごとのルールを直すのは業務マスターの「通知プリセット」です。
      </p>

      <div className="mb-3 flex items-center justify-between rounded-sm border border-wpborder bg-white px-3 py-2">
        <span className={suppressed ? 'text-wpnotice-error' : 'text-wptext-muted'}>
          {suppressed
            ? 'このイベントの通知は停止中です（発火時刻が来てもスキップされます）'
            : 'このイベントの通知は有効です'}
        </span>
        <div className="flex gap-2">
          <button type="button" disabled={busy} onClick={() => setAddOpen(true)} className="btn-secondary">
            この回だけメールを足す
          </button>
          <button type="button" disabled={busy} onClick={() => void onToggleSuppression()} className="btn-secondary">
            {suppressed ? '停止を解除する' : '通知を停止する'}
          </button>
        </div>
      </div>

      {schedules.length === 0 ? (
        <p className="text-wptext-muted">
          配信予定はありません。イベント種別に通知プリセットが設定されているか確認するか、「この回だけメールを足す」から追加してください。
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-left">
            <thead>
              <tr className="border-b border-wpborder bg-wpcanvas/60">
                <th className="px-2 py-1.5">文面</th>
                <th className="px-2 py-1.5">宛先</th>
                <th className="px-2 py-1.5">配信時刻</th>
                <th className="px-2 py-1.5">状態</th>
                <th className="px-2 py-1.5" />
              </tr>
            </thead>
            <tbody>
              {schedules.map((schedule) => (
                <tr key={schedule.id} className="border-b border-wpborder">
                  <td className="px-2 py-1.5">
                    {schedule.manual_subject ?? schedule.template_name ?? schedule.rule_key}
                    {schedule.condition && (
                      <span className="ml-1 text-wptext-muted">
                        （{CONDITION_LABELS[schedule.condition] ?? schedule.condition}）
                      </span>
                    )}
                    {schedule.is_manual && (
                      <span className="ml-1 text-wptext-muted">（手入力）</span>
                    )}
                    {schedule.rule_key.startsWith('event-extra-') && (
                      <span className="ml-1 text-wptext-muted">（この回だけ追加）</span>
                    )}
                    {schedule.attachment_name && (
                      <span className="ml-1 text-wptext-muted">（添付: {schedule.attachment_name}）</span>
                    )}
                  </td>
                  <td className="px-2 py-1.5">
                    {AUDIENCE_LABELS[schedule.audience_key] ?? schedule.audience_key}
                  </td>
                  <td className="px-2 py-1.5">
                    {formatDateTime(schedule.scheduled_for)}
                    {schedule.time_overridden && (
                      <span className="ml-1 text-wptext-muted">（この回だけ変更）</span>
                    )}
                  </td>
                  <td className="px-2 py-1.5">
                    {SCHEDULE_STATUS_LABELS[schedule.status]}
                    {schedule.note && (
                      <span className="ml-1 text-wptext-muted">（{schedule.note}）</span>
                    )}
                  </td>
                  <td className="px-2 py-1.5">
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => void onPreview(schedule)}
                        className="text-wpaccent hover:underline"
                      >
                        プレビュー
                      </button>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => void onTestSend(schedule)}
                        className="text-wpaccent hover:underline"
                      >
                        テスト送信
                      </button>
                      {schedule.status === 'scheduled' && (
                        <>
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => {
                              setRescheduleTarget(schedule);
                              setRescheduleAt(toDatetimeLocalValue(schedule.scheduled_for));
                            }}
                            className="text-wpaccent hover:underline"
                          >
                            時刻を変える
                          </button>
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => void onCancel(schedule)}
                            className="text-wpnotice-error hover:underline"
                          >
                            キャンセル
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal open={preview !== null} title="配信プレビュー" onClose={() => setPreview(null)} wide>
        {preview && (
          <>
            <dl className="flex flex-col gap-1 text-wptext-muted">
              <div>
                <dt className="inline font-semibold">配信時刻: </dt>
                <dd className="inline">{formatDateTime(preview.scheduled_for)}</dd>
              </div>
              {preview.condition && (
                <div>
                  <dt className="inline font-semibold">条件: </dt>
                  <dd className="inline">
                    {CONDITION_LABELS[preview.condition] ?? preview.condition}（現時点で
                    {preview.condition_met ? '送信される' : '送信されない'}）
                  </dd>
                </div>
              )}
              <div>
                <dt className="inline font-semibold">宛先（{preview.recipients.length}名）: </dt>
                <dd className="inline">
                  {preview.recipients.map((r) => r.display_name ?? r.email).join('、') || 'なし'}
                </dd>
              </div>
              {preview.attachment_name && (
                <div>
                  <dt className="inline font-semibold">添付: </dt>
                  <dd className="inline">{preview.attachment_name}</dd>
                </div>
              )}
            </dl>
            <div className="mt-2 border-t border-wpborder pt-2">
              <p className="font-semibold text-wptext-strong">{preview.subject}</p>
              <p className="mt-1 whitespace-pre-wrap text-wptext-muted">{preview.body}</p>
            </div>
          </>
        )}
      </Modal>

      <Modal
        open={rescheduleTarget !== null}
        title="この回だけの配信時刻"
        onClose={() => setRescheduleTarget(null)}
      >
        <p className="mb-3 text-wptext-muted">
          種類の自動配信ルールは変わりません。この回のこの1通だけ、送る時刻を変えます。
        </p>
        <label className="block">
          <span className="mb-1.5 block text-wptext-muted">配信時刻</span>
          <input
            type="datetime-local"
            value={rescheduleAt}
            onChange={(e) => setRescheduleAt(e.target.value)}
            className="w-full rounded-sm border border-wpborder px-3 py-2"
          />
        </label>
        <div className="mt-4 flex gap-2">
          <button
            type="button"
            disabled={busy || !rescheduleAt}
            onClick={() => void onReschedule()}
            className="rounded-sm border border-transparent bg-wpaccent px-3.5 py-2 text-white hover:bg-wpaccent-hover disabled:opacity-50"
          >
            変更する
          </button>
          <button type="button" onClick={() => setRescheduleTarget(null)} className="btn-secondary">
            閉じる
          </button>
        </div>
      </Modal>

      <Modal
        open={addOpen}
        title="この回だけメールを足す"
        onClose={() => {
          setAddOpen(false);
          resetAddForm();
        }}
        wide
      >
        <p className="mb-3 text-wptext-muted">
          種類の自動配信ルールには残りません。この回の予約に1通だけ足します。件名・本文はその場で書けます。添付はメールにのみ付きます。
        </p>
        <label className="mb-3 block">
          <span className="mb-1.5 block text-wptext-muted">宛先</span>
          <select
            value={addAudience}
            onChange={(e) => setAddAudience(e.target.value)}
            className="w-full rounded-sm border border-wpborder px-3 py-2"
          >
            {EXTRA_AUDIENCES.map((key) => (
              <option key={key} value={key}>
                {AUDIENCE_LABELS[key]}
              </option>
            ))}
          </select>
        </label>
        <label className="mb-3 block">
          <span className="mb-1.5 block text-wptext-muted">件名</span>
          <input
            type="text"
            value={addSubject}
            onChange={(e) => setAddSubject(e.target.value)}
            className="w-full rounded-sm border border-wpborder px-3 py-2"
            maxLength={255}
          />
        </label>
        <label className="mb-3 block">
          <span className="mb-1.5 block text-wptext-muted">本文</span>
          <textarea
            value={addBody}
            onChange={(e) => setAddBody(e.target.value)}
            rows={8}
            className="w-full rounded-sm border border-wpborder px-3 py-2"
          />
        </label>
        <label className="mb-3 block">
          <span className="mb-1.5 block text-wptext-muted">送信名義（任意）</span>
          <input
            type="text"
            value={addSenderName}
            onChange={(e) => setAddSenderName(e.target.value)}
            className="w-full rounded-sm border border-wpborder px-3 py-2"
            maxLength={120}
            placeholder="未入力ならシステムの既定名義"
          />
        </label>
        <label className="mb-3 block">
          <span className="mb-1.5 block text-wptext-muted">添付ファイル（任意）</span>
          <input
            type="file"
            onChange={(e) => setAddFile(e.target.files?.[0] ?? null)}
            className="w-full rounded-sm border border-wpborder px-3 py-2"
          />
          {addFile && (
            <span className="mt-1 block text-wptext-muted">
              {addFile.name}（{Math.ceil(addFile.size / 1024)} KB）
            </span>
          )}
        </label>
        <label className="block">
          <span className="mb-1.5 block text-wptext-muted">配信時刻</span>
          <input
            type="datetime-local"
            value={addAt}
            onChange={(e) => setAddAt(e.target.value)}
            className="w-full rounded-sm border border-wpborder px-3 py-2"
          />
        </label>
        <div className="mt-4 flex gap-2">
          <button
            type="button"
            disabled={busy || !canAdd}
            onClick={() => void onAddExtra()}
            className="rounded-sm border border-transparent bg-wpaccent px-3.5 py-2 text-white hover:bg-wpaccent-hover disabled:opacity-50"
          >
            追加する
          </button>
          <button
            type="button"
            onClick={() => {
              setAddOpen(false);
              resetAddForm();
            }}
            className="btn-secondary"
          >
            閉じる
          </button>
        </div>
      </Modal>
    </div>
  );
}
